import { useMsal } from '@azure/msal-react'
import { faArrowRight, faPlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Button,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { LocationCardSkeleton } from '../../components/home/LocationsSkeleton'
import LocationCard from '../../components/location/LocationCard'
import { fetchLocationsByOrganizationId } from '../../data/location.service'
import { fetchMyOrganization } from '../../data/organization.service'
import { Location } from '../../models/Location'
import { OnboardingOrganization } from '../../models/Onboarding/OnboardingOrganization'
import { HostOnboardingSteps } from '../../utils/constants'
import { getCurrentStepFromLocation } from '../../utils/locationUtils'

export default function MyLocationsPage() {
  const navigate = useNavigate()
  const { instance } = useMsal()
  const [organization, setOrganization] =
    useState<OnboardingOrganization | null>(null)
  const [locations, setLocations] = useState<Location[]>([])
  const [incompleteLocation, setIncompleteLocation] = useState<Location | null>(
    null,
  )
  const [isLoading, setIsLoading] = useState(true)
  const skeletonArray = new Array(8).fill(0)

  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        const org = await fetchMyOrganization(instance)
        setOrganization(org)
      } catch (error) {
        console.error('Error fetching organization:', error)
      }
    }

    fetchOrganization()
  }, [instance])

  useEffect(() => {
    const fetchLocations = async () => {
      if (!organization) return

      try {
        const resp = await fetchLocationsByOrganizationId(organization.id)
        setLocations(resp)

        // Find the first incomplete location
        const incomplete = resp.find((location) => {
          const currentStep = getCurrentStepFromLocation(location)
          return currentStep !== HostOnboardingSteps.Review
        })
        setIncompleteLocation(incomplete || null)
      } catch (ex) {
        notifications.show({
          title: 'An unknown error occurred',
          message: 'Unable to get locations. Please try again.',
          color: 'red',
          autoClose: false,
        })
      } finally {
        setIsLoading(false)
      }
    }

    fetchLocations()
  }, [organization])

  if (!organization) {
    return null
  }

  return (
    <Stack h='100%' gap='xl'>
      <Group
        justify='space-between'
        align='center'
        h={70}
        px={24}
        style={{
          boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Title fw={500} fz={{ base: 'h3', sm: 'h2' }}>
          My Locations
        </Title>
        {locations.length > 0 && (
          <Button
            onClick={() => navigate('/locations/add')}
            leftSection={<FontAwesomeIcon icon={faPlus} />}
          >
            Add Location
          </Button>
        )}
      </Group>

      {incompleteLocation && (
        <Paper
          withBorder
          p='md'
          mx={24}
          radius='md'
          style={{
            backgroundColor: '#f8f9fa',
          }}
        >
          <Group align='flex-start'>
            <Stack gap='xs' style={{ flex: 1 }}>
              <Text fw={500} size='lg'>
                Complete Your Location Setup
              </Text>
              <Text size='sm' c='dimmed'>
                Your location "{incompleteLocation.name}" needs additional
                information to start accepting advertisements.
              </Text>
              <Button
                variant='light'
                rightSection={<FontAwesomeIcon icon={faArrowRight} />}
                onClick={() =>
                  navigate(`/locations/edit/${incompleteLocation.id}`)
                }
                style={{ alignSelf: 'flex-start' }}
              >
                Complete Location Details
              </Button>
            </Stack>
          </Group>
        </Paper>
      )}

      {!isLoading && locations.length === 0 ? (
        <Stack align='center' py={20} h='100%' justify='center'>
          <Text size='xl' c='dimmed'>
            No locations found
          </Text>
          <Button variant='light' onClick={() => navigate('/hostOnboarding')}>
            Complete Business Profile
          </Button>
        </Stack>
      ) : (
        <SimpleGrid
          cols={{ base: 1, sm: 2, md: 3, lg: 3, xl: 4 }}
          spacing={50}
          px={24}
          pb={24}
          style={{ alignItems: 'flex-start' }}
        >
          {isLoading
            ? skeletonArray.map((_, index) => (
                <LocationCardSkeleton key={index} />
              ))
            : locations.map((l) => <LocationCard location={l} key={l.id} />)}
        </SimpleGrid>
      )}
    </Stack>
  )
}
